import { useConfiguration } from '../../../../store/configurationStore';
import {
  Rule,
  Label,
  RuleColumn,
  RuleCondition,
  SectionId,
  SecurityType,
  getRulesSectionId,
} from '../types';

export const useRulesEngine = (sectionId: SectionId) => {
  const {
    templates,
    activeTemplateId,
    activeInstrumentType,
    addRule,
    updateRule,
    deleteRule,
    addColumnToRule,
    removeColumnFromRule,
    updateRuleColumn,
    addLabelToRuleColumn,
    removeLabelFromRuleColumn,
  } = useConfiguration();

  // Get the active template and instrument config
  const activeTemplate = templates.find(t => t.id === activeTemplateId) || null;
  const activeInstrumentConfig = activeTemplate?.instrumentConfigs.find(
    ic => ic.instrumentType === activeInstrumentType
  );

  // INSTRUMENT_IDENTIFIERS uses inline rules, others use separate rules section
  let rules: Rule[] = [];
  if (sectionId === SectionId.INSTRUMENT_IDENTIFIERS) {
    // Access inline rules directly from INSTRUMENT_IDENTIFIERS section
    const section = activeInstrumentConfig?.sections.find(
      section => section.id === sectionId
    );
    rules = section?.config.rules || [];
  } else {
    // Other sections use separate rules sections
    const rulesSectionId = getRulesSectionId(sectionId);
    const rulesSection = activeInstrumentConfig?.sections.find(
      section => section.id === rulesSectionId
    );
    rules = rulesSection?.config.rules || [];
  }

  // Create a new rule with one empty column
  const createRule = (name: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    const newRule: Rule = {
      id: `rule-${Date.now()}`,
      name,
      columns: [
        {
          id: `col-${Date.now()}-${Math.random()}`,
          sourceColumn: '',
          labels: [],
        },
      ],
      conditions: [],
    };

    addRule(activeTemplateId, activeInstrumentType, sectionId, newRule);
  };

  // Update an existing rule
  const updateRuleDetails = (ruleId: string, updates: Partial<Rule>): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    updateRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      updates
    );
  };

  // Delete a rule
  const removeRule = (ruleId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    deleteRule(activeTemplateId, activeInstrumentType, sectionId, ruleId);
  };

  // Add a new column to a rule
  const addColumn = (ruleId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    const newColumn: RuleColumn = {
      id: `col-${Date.now()}-${Math.random()}`,
      sourceColumn: '',
      labels: [],
    };

    addColumnToRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      newColumn
    );
  };

  // Add a Security Type condition column to a rule
  const addSecurityTypeColumn = (ruleId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    const newColumn: RuleColumn = {
      id: `col-${Date.now()}-${Math.random()}`,
      columnType: 'SECURITY_TYPE',
      securityType: 'OTC',
      sourceColumn: '',
      labels: [],
    };

    addColumnToRule(activeTemplateId, activeInstrumentType, sectionId, ruleId, newColumn);
  };

  // Update the securityType on a SECURITY_TYPE column
  const updateColumnSecurityType = (ruleId: string, columnId: string, securityType: SecurityType): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    updateRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      columnId,
      { securityType }
    );
  };

  // Remove a column from a rule
  const removeColumn = (ruleId: string, columnId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    removeColumnFromRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      columnId
    );
  };

  // Update column source
  const updateColumnSource = (
    ruleId: string,
    columnId: string,
    sourceColumn: string
  ): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    updateRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      columnId,
      { sourceColumn }
    );
  };

  // Add label to a specific column in a rule
  const addLabelToColumn = (
    ruleId: string,
    columnId: string,
    value: string
  ): void => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      throw new Error(`⚠️ Rule with id "${ruleId}" not found`);
    }

    const column = rule.columns.find(c => c.id === columnId);
    if (!column) {
      throw new Error(`⚠️ Column with id "${columnId}" not found`);
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = column.labels.some(
      label => label.value.toLowerCase() === value.toLowerCase()
    );

    if (isDuplicate) {
      throw new Error(`⚠️ Label "${value}" already exists in this column`);
    }

    const newLabel: Label = {
      id: `label-${Date.now()}-${Math.random()}`,
      value,
    };

    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    // Use new action - it reads current state inside set()
    addLabelToRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      columnId,
      newLabel
    );
  };

  // Remove label from a specific column in a rule
  const removeLabelFromColumn = (
    ruleId: string,
    columnId: string,
    labelId: string
  ): void => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      throw new Error(`⚠️ Rule with id "${ruleId}" not found`);
    }

    const column = rule.columns.find(c => c.id === columnId);
    if (!column) {
      throw new Error(`⚠️ Column with id "${columnId}" not found`);
    }

    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    // Call focused store action - it reads CURRENT state
    removeLabelFromRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      ruleId,
      columnId,
      labelId
    );
  };

  // Validate rule completeness - checks if columns have sources and labels
  const validateRule = (
    ruleId: string
  ): { isValid: boolean; warnings: string[] } => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      return { isValid: false, warnings: ['Rule not found'] };
    }

    const warnings: string[] = [];

    // Check if any column has empty sourceColumn
    const emptyColumns = rule.columns.filter(
      col => !col.sourceColumn || col.sourceColumn.trim() === ''
    );
    if (emptyColumns.length > 0) {
      warnings.push('⚠️ Some columns have no source selected');
    }

    // Check if any column has no labels
    const columnsWithoutLabels = rule.columns.filter(
      col => col.labels.length === 0
    );
    if (columnsWithoutLabels.length > 0) {
      warnings.push('⚠️ Some columns have no labels');
    }

    // Check for duplicate column selections
    const columnSources = rule.columns
      .map(col => col.sourceColumn)
      .filter(source => source && source.trim() !== '');
    const duplicates = columnSources.filter(
      (source, index) => columnSources.indexOf(source) !== index
    );
    if (duplicates.length > 0) {
      warnings.push('⚠️ Some columns are selected multiple times');
    }

    // Rule is valid if it has at least one complete column (with source and labels)
    const hasCompleteColumn = rule.columns.some(
      col => col.sourceColumn.trim() !== '' && col.labels.length > 0
    );

    return { isValid: hasCompleteColumn, warnings };
  };

  return {
    rules,
    createRule,
    updateRuleDetails,
    removeRule,
    addColumn,
    addSecurityTypeColumn,
    updateColumnSecurityType,
    removeColumn,
    updateColumnSource,
    addLabelToColumn,
    removeLabelFromColumn,
    validateRule,
  };
};
